<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WabaCallingAPI from './api';
import { useWabaCallingStore } from './store';

const props = defineProps({
  inbox: { type: Object, default: () => ({}) },
  chat: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const { currentAccount } = useAccount();
const callingStore = useWabaCallingStore();
const configured = ref(false);
const loading = ref(true);

const available = computed(
  () =>
    currentAccount.value?.feature_modules?.whatsapp_waba_calls === true &&
    props.inbox?.channel_type === 'Channel::Whatsapp' &&
    props.inbox?.provider === 'whatsapp_cloud' &&
    configured.value
);
const contactId = computed(() => props.chat?.meta?.sender?.id);
const disabled = computed(
  () =>
    callingStore.busy ||
    !contactId.value ||
    !!callingStore.active ||
    callingStore.incoming.length > 0
);

const load = async () => {
  if (!props.inbox?.id) return;
  loading.value = true;
  try {
    const { data } = await WabaCallingAPI.settings(props.inbox.id);
    configured.value = data.enabled === true;
  } catch (error) {
    configured.value = false;
  } finally {
    loading.value = false;
  }
};

const start = async () => {
  const result = await callingStore.start(props.inbox.id, contactId.value);
  if (result.permission) {
    useAlert(t('ASTRACHAT_WABA_CALLING.ALERT.PERMISSION_REQUESTED'));
  } else if (result.error) {
    if (result.error === 'whatsapp_calling_payment_required') {
      useAlert(t('ASTRACHAT_WABA_CALLING.ALERT.PAYMENT_REQUIRED'));
    } else if (result.error === 'missing_call_permission') {
      useAlert(t('ASTRACHAT_WABA_CALLING.ALERT.PERMISSION_REQUIRED'));
    } else if (result.error === 'call_permission_rate_limited') {
      useAlert(t('ASTRACHAT_WABA_CALLING.ALERT.PERMISSION_RATE_LIMITED'));
    } else {
      useAlert(t('ASTRACHAT_WABA_CALLING.ALERT.CALL_FAILED'));
    }
  }
};

// The conversation header is mounted before the inbox store can finish loading.
// Watching the id keeps the action available after the asynchronous inbox lookup.
watch(
  () => props.inbox?.id,
  inboxId => {
    if (inboxId) load();
  },
  { immediate: true }
);
</script>

<template>
  <NextButton
    v-show="available"
    v-tooltip.bottom="t('ASTRACHAT_WABA_CALLING.ACTION.START')"
    sm
    ghost
    slate
    icon="i-lucide-phone"
    :is-loading="loading || callingStore.busy"
    :disabled="disabled"
    @click="start"
  />
</template>
